import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuthStore } from '../../domains/identity/store/authStore';

// ─── Icons ────────────────────────────────────────────────────────────────────

function IconCalendar({ sm }) {
  const s = sm ? 'w-4 h-4' : 'w-[18px] h-[18px]';
  return (
    <svg className={s} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

function IconDocument({ sm }) {
  const s = sm ? 'w-4 h-4' : 'w-[18px] h-[18px]';
  return (
    <svg className={s} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function IconTag({ sm }) {
  const s = sm ? 'w-4 h-4' : 'w-[18px] h-[18px]';
  return (
    <svg className={s} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
      />
    </svg>
  );
}

function IconGrid({ sm }) {
  const s = sm ? 'w-4 h-4' : 'w-[18px] h-[18px]';
  return (
    <svg className={s} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
      />
    </svg>
  );
}

function IconGlobe({ sm }) {
  const s = sm ? 'w-4 h-4' : 'w-[18px] h-[18px]';
  return (
    <svg className={s} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 004 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function IconShield({ sm }) {
  const s = sm ? 'w-4 h-4' : 'w-[18px] h-[18px]';
  return (
    <svg className={s} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );
}

function IconUser({ sm }) {
  const s = sm ? 'w-4 h-4' : 'w-[18px] h-[18px]';
  return (
    <svg className={s} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );
}

// ─── Nav config ───────────────────────────────────────────────────────────────

const BASE_NAV = [
  { label: 'Home', short: 'Home', path: '/', Icon: IconGrid },
  { label: 'Elections', short: 'Elections', path: '/elections', Icon: IconCalendar },
];

const ADMIN_NAV = [
  { label: 'Authority Editor', path: '/admin/authorities', Icon: IconShield },
  { label: 'Scrapers', path: '/admin/scrapers', Icon: IconTag },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function Header() {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuthStore();

  // Track keyboard visibility on mobile
  useEffect(() => {
    const handleFocus = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        setKeyboardVisible(true);
      }
    };
    const handleBlur = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        setKeyboardVisible(false);
      }
    };
    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('blur', handleBlur, true);
    return () => {
      document.removeEventListener('focus', handleFocus, true);
      document.removeEventListener('blur', handleBlur, true);
    };
  }, []);

  const navItems = [
    ...BASE_NAV,
    ...(isAuthenticated
      ? [{ label: 'Dashboard', short: 'Dash', path: '/dashboard', Icon: IconGrid }]
      : []),
  ];

  // Close user menu on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  // Close user menu on route change
  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    navigate('/login');
  };

  return (
    <>
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>

      {/* ── Desktop sidebar — BULLETIN BOARD ─────────────────────────────── */}
      <aside
        className="hidden md:flex flex-col w-56 shrink-0 sticky top-0 h-screen"
        style={{
          background: '#ede5d4',
          borderRight: '3px solid #1a1a1a',
        }}
      >
        {/* Logo — marker on cardboard */}
        <Link
          to="/"
          className="flex items-center gap-2.5 px-5 h-16 shrink-0 group no-underline active:opacity-75"
          style={{ borderBottom: '3px solid #1a1a1a' }}
        >
          {/* Hand-drawn checkmark */}
          <div
            className="shrink-0 flex items-center justify-center"
            style={{
              width: 32,
              height: 32,
              background: '#d62828',
              border: '2px solid #1a1a1a',
              transform: 'rotate(-3deg)',
            }}
          >
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              strokeWidth={3}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <span
            className="group-hover:text-secondary-500 transition-colors"
            style={{
              fontFamily: "'Permanent Marker', cursive",
              fontSize: '18px',
              color: '#1a1a1a',
              letterSpacing: '0.02em',
            }}
          >
            VotePlan
          </span>
        </Link>

        {/* Section label — typewriter style */}
        <p
          className="px-4 pt-5 pb-2 select-none"
          style={{
            fontFamily: "'Special Elite', monospace",
            fontSize: '10px',
            letterSpacing: '0.15em',
            color: '#8a7e6b',
            textTransform: 'uppercase',
          }}
        >
          {/* NAVIGATE */}
        </p>

        {/* Nav items — hand-labeled tabs */}
        <nav
          className="flex-1 px-3 flex flex-col gap-1.5 overflow-y-auto pb-2"
          aria-label="Main navigation"
        >
          {navItems.map(({ label, path, Icon }) => {
            const isActive = location.pathname === path;
            return (
              <Link
                key={path}
                to={path}
                className="flex items-center gap-3 px-3.5 py-2.5 text-[14px] font-semibold select-none no-underline transition-all duration-75 active:opacity-75"
                style={{
                  fontFamily: "'Patrick Hand', cursive",
                  color: isActive ? '#d62828' : '#3d3528',
                  background: isActive ? '#fefcf6' : 'transparent',
                  border: isActive ? '2px solid #1a1a1a' : '2px solid transparent',
                  boxShadow: isActive ? '3px 3px 0px #1a1a1a' : 'none',
                  transform: isActive ? 'rotate(-0.5deg)' : 'none',
                }}
              >
                <span style={{ color: isActive ? '#d62828' : '#6b5f4d' }}>
                  <Icon />
                </span>
                {label}
                {isActive && (
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: '18px',
                      lineHeight: 1,
                    }}
                  >
                    *
                  </span>
                )}
              </Link>
            );
          })}

          {/* Admin section — only for admins */}
          {user?.role === 'admin' && (
            <>
              <p
                className="px-1 pt-4 pb-1 select-none"
                style={{
                  fontFamily: "'Special Elite', monospace",
                  fontSize: '10px',
                  letterSpacing: '0.15em',
                  color: '#8a7e6b',
                  textTransform: 'uppercase',
                }}
              >
                {/* ADMIN */}
              </p>
              {ADMIN_NAV.map(({ label, path, Icon }) => {
                const isActive = location.pathname.startsWith(path);
                return (
                  <Link
                    key={path}
                    to={path}
                    className="flex items-center gap-3 px-3.5 py-2.5 text-[14px] font-semibold select-none no-underline transition-all duration-75 active:opacity-75"
                    style={{
                      fontFamily: "'Patrick Hand', cursive",
                      color: isActive ? '#f77f00' : '#3d3528',
                      background: isActive ? '#fefcf6' : 'transparent',
                      border: isActive ? '2px solid #1a1a1a' : '2px solid transparent',
                      boxShadow: isActive ? '3px 3px 0px #1a1a1a' : 'none',
                      transform: isActive ? 'rotate(0.5deg)' : 'none',
                    }}
                  >
                    <span style={{ color: isActive ? '#f77f00' : '#6b5f4d' }}>
                      <Icon />
                    </span>
                    {label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* User section */}
        <div className="px-3 py-4 shrink-0" style={{ borderTop: '3px solid #1a1a1a' }}>
          {isAuthenticated ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen((v) => !v)}
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
                className="w-full flex items-center gap-2.5 px-3 py-2 transition-colors duration-150"
                style={{
                  fontFamily: "'Patrick Hand', cursive",
                  color: '#1a1a1a',
                  border: '2px solid transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.border = '2px dashed #8a7e6b';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.border = '2px solid transparent';
                }}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center text-white text-sm font-bold shrink-0 select-none"
                  style={{
                    background: '#d62828',
                    border: '2px solid #1a1a1a',
                    fontFamily: "'Permanent Marker', cursive",
                  }}
                >
                  {user?.firstName?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-[14px] font-medium truncate leading-tight">
                    {user?.firstName || 'Account'}
                  </p>
                  <p
                    className="text-[11px] truncate leading-tight"
                    style={{ color: '#8a7e6b', fontFamily: "'Special Elite', monospace" }}
                  >
                    {user?.email || ''}
                  </p>
                </div>
                <svg
                  className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`}
                  style={{ color: '#8a7e6b' }}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  />
                </svg>
              </button>

              {/* Upward dropdown — kraft paper style */}
              <div
                className={`absolute bottom-full left-0 right-0 mb-1.5 overflow-hidden py-1 transition-all duration-150 ${
                  userMenuOpen
                    ? 'opacity-100 scale-100 pointer-events-auto'
                    : 'opacity-0 scale-95 pointer-events-none'
                }`}
                style={{
                  background: '#fefcf6',
                  border: '2px solid #1a1a1a',
                  boxShadow: '3px -3px 0px #1a1a1a',
                  transformOrigin: 'bottom center',
                }}
              >
                <Link
                  to="/settings"
                  className="flex items-center px-4 py-2.5 text-[14px] transition-colors duration-100 no-underline zine-tap"
                  style={{ color: '#3d3528', fontFamily: "'Patrick Hand', cursive" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#ede5d4';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  Settings
                </Link>
                <Link
                  to="/subscription"
                  className="flex items-center px-4 py-2.5 text-[14px] transition-colors duration-100 no-underline zine-tap"
                  style={{ color: '#3d3528', fontFamily: "'Patrick Hand', cursive" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#ede5d4';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  Subscription
                </Link>
                <Link
                  to="/deliveries"
                  className="flex items-center px-4 py-2.5 text-[14px] transition-colors duration-100 no-underline zine-tap"
                  style={{ color: '#3d3528', fontFamily: "'Patrick Hand', cursive" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#ede5d4';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  My Deliveries
                </Link>
                <div style={{ margin: '4px 12px', borderTop: '2px dashed #bfb49e' }} />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-2.5 text-[14px] transition-colors duration-100"
                  style={{ color: '#d62828', fontFamily: "'Patrick Hand', cursive" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#ede5d4';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  Sign Out
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate('/login')}
                className="w-full py-2.5 text-[14px] font-bold transition-colors duration-150"
                style={{
                  fontFamily: "'Patrick Hand', cursive",
                  color: '#1a1a1a',
                  border: '2px dashed #1a1a1a',
                  background: 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fefcf6';
                  e.currentTarget.style.borderStyle = 'solid';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderStyle = 'dashed';
                }}
              >
                Log In
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="w-full py-2.5 text-[14px] font-bold transition-colors duration-150"
                style={{
                  fontFamily: "'Permanent Marker', cursive",
                  color: '#fefcf6',
                  background: '#d62828',
                  border: '2px solid #1a1a1a',
                  boxShadow: '3px 3px 0px #1a1a1a',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translate(-1px, -1px)';
                  e.currentTarget.style.boxShadow = '4px 4px 0px #1a1a1a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '3px 3px 0px #1a1a1a';
                }}
              >
                SIGN UP
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ── Mobile bottom bar — ZINE TABS ────────────────────────────────── */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-50 flex items-stretch transition-transform duration-300"
        style={{
          background: '#ede5d4',
          borderTop: '3px solid #1a1a1a',
          paddingBottom: 'env(safe-area-inset-bottom)',
          transform: keyboardVisible ? 'translateY(100%)' : 'translateY(0)',
        }}
        aria-label="Mobile navigation"
      >
        {navItems.slice(0, 4).map(({ short, path, Icon }) => {
          const isActive = location.pathname === path;
          return (
            <Link
              key={path}
              to={path}
              className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5 no-underline transition-all duration-75 mx-0.5 my-1 active:opacity-75"
              style={{
                fontFamily: "'Patrick Hand', cursive",
                fontSize: '11px',
                fontWeight: 700,
                color: isActive ? '#d62828' : '#6b5f4d',
                background: isActive ? '#fefcf6' : 'transparent',
                border: isActive ? '2px solid #1a1a1a' : '2px solid transparent',
                boxShadow: isActive ? '2px 2px 0px #1a1a1a' : 'none',
              }}
            >
              <span style={{ color: isActive ? '#d62828' : '#8a7e6b' }}>
                <Icon sm />
              </span>
              {short}
            </Link>
          );
        })}

        {/* Auth slot */}
        {(() => {
          const path = isAuthenticated ? '/dashboard' : '/login';
          const label = isAuthenticated ? 'Account' : 'Sign In';
          const isActive = location.pathname === path;
          return (
            <Link
              to={path}
              className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5 no-underline transition-all duration-75 mx-0.5 my-1 active:opacity-75"
              style={{
                fontFamily: "'Patrick Hand', cursive",
                fontSize: '11px',
                fontWeight: 700,
                color: isActive ? '#d62828' : '#6b5f4d',
                background: isActive ? '#fefcf6' : 'transparent',
                border: isActive ? '2px solid #1a1a1a' : '2px solid transparent',
                boxShadow: isActive ? '2px 2px 0px #1a1a1a' : 'none',
              }}
            >
              <span style={{ color: isActive ? '#d62828' : '#8a7e6b' }}>
                <IconUser sm />
              </span>
              {label}
            </Link>
          );
        })()}
      </nav>
    </>
  );
}
